"use client";

import { rashis } from "@ishvaram/domain-data/rashis";

import type { ActiveTransit, SadeSatiInfo } from "@/lib/api/transits";
import { SATURN_STATUS_STYLES, type SadeSatiPhase } from "@/lib/saturn-status";

export function RashiPicker({
  selectedRashi,
  onSelect,
  sadeSati,
  saturnTransit,
  isLoading,
  language,
}: {
  selectedRashi: string | null;
  onSelect: (id: string | null) => void;
  sadeSati: SadeSatiInfo | null | undefined;
  saturnTransit: ActiveTransit | null;
  isLoading: boolean;
  language: "en" | "hi";
}) {
  const hi = language === "hi";

  return (
    <div className="card-sacred mb-6 p-5 sm:p-6">
      <p className="mb-4 text-center text-body-small font-semibold text-ish-dark">
        {hi ? "Apni Janma Rashi select karein" : "Select your Moon sign to check live"}
      </p>
      <RashiButtons selectedRashi={selectedRashi} onSelect={onSelect} />
      <SelectedRashiResult
        selectedRashi={selectedRashi}
        sadeSati={sadeSati}
        saturnTransit={saturnTransit}
        isLoading={isLoading}
        hi={hi}
      />
    </div>
  );
}

function RashiButtons({
  selectedRashi,
  onSelect,
}: {
  selectedRashi: string | null;
  onSelect: (id: string | null) => void;
}) {
  return (
    <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
      {rashis.map((rashi) => {
        const isSelected = selectedRashi === rashi.id;
        return (
          <button
            key={rashi.id}
            type="button"
            onClick={() => onSelect(isSelected ? null : rashi.id)}
            className="flex flex-col items-center gap-0.5 rounded-lg border p-2 text-center transition-all duration-150"
            style={{
              borderColor: isSelected ? "var(--ish-accent)" : "var(--ish-border-light)",
              backgroundColor: isSelected ? "var(--ish-accent-light)" : "transparent",
            }}
          >
            <span className="text-body-small font-bold text-ish-dark">{rashi.name_en}</span>
            <span className="text-[10px] text-ish-text-faint">{rashi.name_hi}</span>
          </button>
        );
      })}
    </div>
  );
}

function SelectedRashiResult({
  selectedRashi,
  sadeSati,
  saturnTransit,
  isLoading,
  hi,
}: {
  selectedRashi: string | null;
  sadeSati: SadeSatiInfo | null | undefined;
  saturnTransit: ActiveTransit | null;
  isLoading: boolean;
  hi: boolean;
}) {
  const selectedData = rashis.find((rashi) => rashi.id === selectedRashi);
  if (!selectedData) return null;

  const phase: SadeSatiPhase = sadeSati?.phase ?? "none";
  const styles = SATURN_STATUS_STYLES[phase];
  return (
    <div
      className="mt-4 rounded-lg p-4"
      style={{ backgroundColor: styles.bg, border: `1px solid ${styles.border}` }}
    >
      {isLoading || sadeSati === undefined ? (
        <p className="text-body-small text-ish-text-muted">
          {hi ? "Live Shani transit check ho raha hai…" : "Checking the live Saturn transit…"}
        </p>
      ) : (
        <ResolvedRashiStatus
          nameEn={selectedData.name_en}
          nameHi={selectedData.name_hi}
          sadeSati={sadeSati}
          saturnTransit={saturnTransit}
          styles={styles}
          hi={hi}
        />
      )}
    </div>
  );
}

function ResolvedRashiStatus({
  nameEn,
  nameHi,
  sadeSati,
  saturnTransit,
  styles,
  hi,
}: {
  nameEn: string;
  nameHi: string;
  sadeSati: SadeSatiInfo | null;
  saturnTransit: ActiveTransit | null;
  styles: (typeof SATURN_STATUS_STYLES)[SadeSatiPhase];
  hi: boolean;
}) {
  const badge = sadeSati ? `${sadeSati.phase.toUpperCase()} PHASE` : "NOT ACTIVE";
  const description = getStatusDescription(sadeSati, saturnTransit, hi);
  return (
    <>
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="text-[16px] font-bold" style={{ color: styles.text }}>
          {nameEn} ({nameHi})
        </span>
        <span
          className="rounded px-2.5 py-1 text-[11px] font-semibold"
          style={{ backgroundColor: styles.badgeBg, color: styles.badge }}
        >
          {badge}
        </span>
      </div>
      <p className="text-body-small" style={{ color: styles.text }}>
        {description}
      </p>
    </>
  );
}

function getStatusDescription(
  sadeSati: SadeSatiInfo | null,
  saturnTransit: ActiveTransit | null,
  hi: boolean
) {
  if (sadeSati && hi) {
    return `${sadeSati.phase_description_en} Yeh phase live engine position se nikla hai.`;
  }
  if (sadeSati) return sadeSati.phase_description_en;

  const saturnSign = saturnTransit?.current_rashi_name_en;
  if (hi) {
    return `Shani ${saturnSign ?? "current rashi"} me hai; is Janma Rashi ke liye Sade Sati active nahi hai.`;
  }
  return `Shani in ${saturnSign ?? "its current sign"} is not in the 12th, 1st, or 2nd sign from this Moon sign.`;
}
